import React from 'react';
import { CheckCircle, XCircle, AlertCircle, Download } from 'lucide-react';
import { ImportResult } from '@/types/data-import.types';
import { useNavigate } from 'react-router-dom';

interface ImportResultsProps {
  result: ImportResult;
  onClose: () => void;
  onImportMore: () => void;
}

const ImportResults: React.FC<ImportResultsProps> = ({
  result,
  onClose,
  onImportMore,
}) => {
  const navigate = useNavigate();

  const handleViewLoans = () => {
    navigate('/loans');
    onClose();
  };

  const hasErrors = result.errors && result.errors.length > 0;
  const hasWarnings = result.warnings && result.warnings.length > 0;
  const isPartialSuccess = result.imported_count > 0 && result.failed_count > 0;

  return (
    <div className="py-8" data-test-id="import-results">
      {/* Summary Icon and Title */}
      <div className="text-center mb-6">
        {result.success && !isPartialSuccess ? (
          <>
            <CheckCircle
              className="w-16 h-16 text-green-600 mx-auto mb-4"
              data-test-id="import-success-icon"
            />
            <h3
              className="text-xl font-semibold text-gray-900"
              data-test-id="import-success-title"
            >
              Import Successful!
            </h3>
          </>
        ) : isPartialSuccess ? (
          <>
            <AlertCircle
              className="w-16 h-16 text-yellow-600 mx-auto mb-4"
              data-test-id="import-warning-icon"
            />
            <h3
              className="text-xl font-semibold text-gray-900"
              data-test-id="import-partial-title"
            >
              Import Completed with Issues
            </h3>
          </>
        ) : (
          <>
            <XCircle
              className="w-16 h-16 text-red-600 mx-auto mb-4"
              data-test-id="import-error-icon"
            />
            <h3
              className="text-xl font-semibold text-gray-900"
              data-test-id="import-failed-title"
            >
              Import Failed
            </h3>
          </>
        )}
      </div>

      {/* Summary Stats */}
      <div
        className="bg-gray-50 rounded-lg p-6 mb-6"
        data-test-id="import-summary"
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center" data-test-id="import-summary-success">
            <p className="text-3xl font-bold text-green-600">
              {result.imported_count}
            </p>
            <p className="text-sm text-gray-600">Loans Imported</p>
          </div>
          <div className="text-center" data-test-id="import-summary-failed">
            <p className="text-3xl font-bold text-red-600">
              {result.failed_count}
            </p>
            <p className="text-sm text-gray-600">Failed</p>
          </div>
        </div>
      </div>

      {/* Errors Section */}
      {hasErrors && (
        <div className="mb-6" data-test-id="import-errors-section">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Errors:</h4>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-40 overflow-y-auto">
            <ul
              className="space-y-1 text-sm text-red-800"
              data-test-id="import-errors-list"
            >
              {result.errors!.slice(0, 5).map((error, index) => (
                <li key={index} data-test-id={`import-error-${index}`}>
                  Row {error.row}: {error.field} - {error.message}
                </li>
              ))}
              {result.errors!.length > 5 && (
                <li className="font-medium" data-test-id="import-errors-more">
                  ... and {result.errors!.length - 5} more errors
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Warnings Section */}
      {hasWarnings && (
        <div className="mb-6" data-test-id="import-warnings-section">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Warnings:</h4>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-h-40 overflow-y-auto">
            <ul
              className="space-y-1 text-sm text-yellow-800"
              data-test-id="import-warnings-list"
            >
              {result.warnings!.slice(0, 5).map((warning, index) => (
                <li key={index} data-test-id={`import-warning-${index}`}>
                  {warning}
                </li>
              ))}
              {result.warnings!.length > 5 && (
                <li className="font-medium" data-test-id="import-warnings-more">
                  ... and {result.warnings!.length - 5} more warnings
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div
        className="flex flex-col sm:flex-row gap-3 justify-center"
        data-test-id="import-results-actions"
      >
        {result.imported_count > 0 && (
          <button
            onClick={handleViewLoans}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors flex items-center justify-center"
            data-test-id="import-view-loans-button"
          >
            View Imported Loans
          </button>
        )}

        <button
          onClick={onImportMore}
          className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors flex items-center justify-center"
          data-test-id="import-more-button"
        >
          Import More Loans
        </button>

        {hasErrors && (
          <button
            className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors flex items-center justify-center"
            data-test-id="import-download-report-button"
            title="Download error report (coming soon)"
            disabled
          >
            <Download className="w-4 h-4 mr-2" />
            Download Report
          </button>
        )}
      </div>
    </div>
  );
};

export default ImportResults;
